class SlideShow extends HTMLElement {
  swiperInstance = null
  #controller = new AbortController()
  #circle = null
  #circleMax = 115
  #circleInterval = null
  #resizeObserver = null

  static #zoomImgStyle = `
    .swiper-slide img {
      transition: transform 0.8s cubic-bezier(.47,1.64,.41,.8);
      will-change: transform;
    }
  `

  #zoomEnabled = false
  #lastScrollY = window.scrollY
  #zoomValue = 1
  #zoomMin = 1
  #zoomMax = 1.1
  #scrollListener = null
  #isLoaded = false
  #slides
  #loader

  connectedCallback () {
    const { signal } = this.#controller
    this.#zoomEnabled = this.dataset.zoomEnabled === 'true'
    this.#loader = this.querySelector(".loader__swiper-div")
    this.#slides = this.querySelectorAll('.swiper-wrapper > *')
    this.#hasAllClass(this.#slides)
    this.#initSwiper()
    window.addEventListener('resize', this.#handleResize, { signal })

    this.addEventListener('mouseenter', () =>
      this.swiperInstance?.autoplay?.stop?.()
    )
    this.addEventListener('mouseleave', () =>
      this.swiperInstance?.autoplay?.start?.()
    )

    // Circle SVG Animation
    this.#circle = this.querySelector('.circle-svg__circle-1')
    if (this.#circle) {
      this.#startCircleAnimation()
    }

    // Inject transition style for smooth zoom (only once)
    if (!document.getElementById('slide-show-zoom-style')) {
      const style = document.createElement('style')
      style.id = 'slide-show-zoom-style'
      style.innerHTML = SlideShow.#zoomImgStyle
      document.head.appendChild(style)
    }

    if (this.#zoomEnabled) {
      this.#addScrollZoomListener()
    }
  }

  disconnectedCallback () {
    this.#controller.abort()
    this.#destroySwiper()
    this.#stopCircleAnimation()
    if (this.#zoomEnabled) {
      this.#removeScrollZoomListener()
    }
  }

  get swiperContainer () {
    return this.querySelector('.swiper')
  }

  #hideLoader () {
    if(this.#loader){
      this.#loader.classList.add("hide_loader")
    }
  }

  #initSwiper () {
    if (!this.swiperContainer) return
    const isMobile = window.innerWidth <= 768
    const disableMobile = this.dataset.disableOnMobile === 'true'
    const disableDesktop = this.dataset.disableOnDesktop === 'true'
    if ((isMobile && disableMobile) || (!isMobile && disableDesktop)) return

    if (typeof Swiper === 'undefined') {
      console.error('[slide-show] Swiper not loaded.')
      return
    }
    const paginationType = this.dataset.paginationType || 'bullets'

    const swiperOptions = {
      loop: this.dataset.loop === 'true',
      speed: parseInt(this.dataset.speed || '1200'),
      spaceBetween: parseInt(this.dataset.spaceBetween || '0'),
      slidesPerView: this.dataset.slidesPerView || 1,
      autoplay:
        this.dataset.autoplay === 'true'
          ? {
              delay: parseInt(this.dataset.autoplayDelay || '4000'),
              disableOnInteraction: false
            }
          : false,
      pagination: {
        el: this.querySelector('.swiper-pagination-el'),
        clickable: true,
        type: paginationType
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      parallax: true,
      on: {
        slideChange: () => {
          this.#playCurrentSlideVideo()
          this.#resetCircle()
          if (this.#zoomEnabled) this.#resetZoom()
        },
        init: () => {
          this.#playCurrentSlideVideo()
          this.#hideLoader()
        }
      },
      breakpoints: this.#parseBreakpoints()
    }
    
    setTimeout(()=>{
      this.swiperInstance = new Swiper(this.swiperContainer, swiperOptions)
    },600)
  }

  #hasAllClass (slides) {
    slides.forEach(slide => {
      slide.classList.add('swiper-slide')
    })

    let allSlides = Array.from(slides)
    let allHaveClass = allSlides.every(el => {
      return el.classList.contains('swiper-slide')
    })

    if (allHaveClass) {
      this.#isLoaded = true
    } else {
      return
    }
  }

  #destroySwiper () {
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true)
      this.swiperInstance = null
    }
  }

  #handleResize = () => {
    this.#destroySwiper()
    this.#initSwiper()
  }

  // ===== Custom Breakpoints Logic =====
  #parseBreakpoints () {
    const customize = this.dataset.customizeBreakpoints === 'true'
    if (customize) {
      const spvMobile = parseInt(this.dataset.slidesPerViewMobile || '1', 10)
      const spvTablet = parseInt(this.dataset.slidesPerViewTablet || '2', 10)
      const spvDesktop = parseInt(this.dataset.slidesPerViewDesktop || '3', 10)
      return {
        0: { slidesPerView: spvMobile },
        640: { slidesPerView: spvTablet },
        1024: { slidesPerView: spvDesktop }
      }
    } else {
      const spvDesktop = parseInt(this.dataset.slidesPerView || '3', 10)
      return {
        0: { slidesPerView: 1 },
        640: { slidesPerView: 1 },
        1024: { slidesPerView: spvDesktop }
      }
    }
  }

  #playCurrentSlideVideo () {
    const slides = this.querySelectorAll('.swiper-slide')
    slides.forEach(slide => {
      const video = slide.querySelector('video')
      if (video) {
        video.pause()
      }
    })

    const activeSlide = this.querySelector('.swiper-slide-active')
    if (activeSlide) {
      const video = activeSlide.querySelector('video')
      if (video) {
        video.play().catch(() => {})
      }
    }
  }

  // ===== Scroll Zoom Logic =====
  #addScrollZoomListener () {
    this.#scrollListener = () => {
      if (!this.#isInViewport()) return
      const currScrollY = window.scrollY
      const direction = currScrollY > this.#lastScrollY ? 'down' : 'up'
      this.#lastScrollY = currScrollY

      const activeSlide = this.querySelector('.swiper-slide-active')
      if (!activeSlide) return
      const img = activeSlide.querySelector('img')
      if (!img) return

      // Apply zoom (with gentle increment and capped at max/min)
      if (direction === 'down') {
        this.#zoomValue = Math.min(this.#zoomValue + 0.03, this.#zoomMax)
      } else {
        this.#zoomValue = Math.max(this.#zoomValue - 0.03, this.#zoomMin)
      }
      img.style.transform = `scale(${this.#zoomValue})`
    }
    window.addEventListener('scroll', this.#scrollListener, { passive: true })
  }

  #removeScrollZoomListener () {
    window.removeEventListener('scroll', this.#scrollListener)
  }

  #resetZoom () {
    // Reset zoom value and image scale on slide change
    this.#zoomValue = 1
    const activeSlide = this.querySelector('.swiper-slide-active')
    if (activeSlide) {
      const img = activeSlide.querySelector('img')
      if (img) img.style.transform = 'scale(1)'
    }
  }

  #isInViewport () {
    // Checks if this slider section is visible in viewport (partial visibility OK)
    const rect = this.getBoundingClientRect()
    return rect.top < window.innerHeight && rect.bottom > 0
  }
  // ===== End Scroll Zoom Logic =====

  // Circle SVG animation
  #startCircleAnimation () {
    let offset = this.#circleMax
    this.#circle.setAttribute('stroke-dashoffset', offset)

    this.#circleInterval = setInterval(() => {
      offset--
      if (offset <= 0) {
        offset = this.#circleMax
        this.swiperInstance?.slideNext()
      }
      this.#circle.setAttribute('stroke-dashoffset', offset)
    }, 40) // Adjust speed here
  }

  #resetCircle () {
    if (!this.#circle) return
    this.#circle.setAttribute('stroke-dashoffset', this.#circleMax)
  }

  #stopCircleAnimation () {
    clearInterval(this.#circleInterval)
  }
}

if (!customElements.get('slide-show')) {
  customElements.define('slide-show', SlideShow)
}
